import {FC, useState, useEffect} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {Modal} from 'react-bootstrap'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {KTIcon} from '../../../../_metronic/helpers'
import {Project} from '../mockData'
import {get_all_projects, update_project, delete_project} from '../api/projectsApi'
import {getStarredProjectIds, toggleStarredProject} from '../utils/starredProjects'
import {LoadingProgressBar} from './LoadingProgressBar'
import {ProjectCardSkeleton} from './ProjectCardSkeleton'
import {toast} from 'react-toastify'
import { permissionCheck } from '../../../helper-functions/PermissionCheck'

const StarredProjects: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [projects, setProjects] = useState<Project[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [projectCategories, setProjectCategories] = useState<Record<string, any[]>>({})
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null)
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null)
  const [editProject, setEditProject] = useState({
    name: '',
    description: '',
    status: 'active' as 'active' | 'inactive',
    category: '',
    tags: [] as string[]
  })
  const canViewProjects = Boolean(permissionCheck('project-view'))
  const canUpdateProject = Boolean(permissionCheck('project-update'))
  const canDeleteProject = Boolean(permissionCheck('project-delete'))
  const canViewProjectLogs = Boolean(permissionCheck('project-activity-log'))

  useEffect(() => {
    if (canViewProjects) {
      fetchStarredProjects()
    }
  }, [canViewProjects, location.pathname])

  // Refresh when component becomes visible (e.g., when navigating back)
  useEffect(() => {
    const handleFocus = () => {
      if (canViewProjects && location.pathname === '/projects/starred') {
        fetchStarredProjects()
      }
    }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [canViewProjects, location.pathname])

  const fetchStarredProjects = async () => {
    try {
      setIsLoading(true)
      const starredIds = getStarredProjectIds()
      
      if (starredIds.length === 0) {
        setProjects([])
        setProjectCategories({})
        setIsLoading(false)
        return
      }

      // Fetch all projects from API
      const response = await get_all_projects()
      
      // API response structure: { status: true, message: "...", data: { all_projects: [...] } }
      const projectsData = response?.data?.all_projects || response?.data?.projects || []
      
      if (Array.isArray(projectsData) && projectsData.length > 0) {
        const categoriesMap: Record<string, any[]> = {}
        
        // Map API response and filter by starred IDs
        const mappedProjects = projectsData
          .filter((project: any) => starredIds.includes(project.id.toString()))
          .map((project: any) => {
            // Calculate URL counts from project URLs if available
            const projectUrls = project.urls || project.project_urls || []
            const urlsArray = Array.isArray(projectUrls) ? projectUrls : []
            const totalUrls = urlsArray.length
            const activeUrls = urlsArray.filter((u: any) => (u.status === 1 || u.status === '1' || u.status === 'active')).length
            
            // Map categories from project response
            const projectCategoriesData = project.project_categories || []
            const categoriesArray = Array.isArray(projectCategoriesData) ? projectCategoriesData : []
            categoriesMap[project.id.toString()] = categoriesArray
            
            // Handle status: 0 = inactive, 1 = active
            const statusValue: 'active' | 'inactive' | 'draft' = project.status === 1 || project.status === '1' ? 'active' : 'inactive'
            
            return {
              id: project.id.toString(),
              name: project.project_name || '',
              description: project.description || '',
              status: statusValue,
              createdAt: project.created_at || '',
              updatedAt: project.updated_at || '',
              updatedByName: project.updated_by?.name || '',
              totalUrls: totalUrls,
              activeUrls: activeUrls,
              isStarred: true,
              category: '',
              tags: []
            }
          })
        
        setProjects(mappedProjects)
        setProjectCategories(categoriesMap)
      } else {
        setProjects([])
        setProjectCategories({})
      }
    } catch (error) {
      console.error('Error fetching starred projects:', error)
      toast.error('Failed to load starred projects')
      setProjects([])
    } finally {
      setIsLoading(false)
    }
  }


  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.category.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/detail/${projectId}`)
  }

  const toggleStar = (projectId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    toggleStarredProject(projectId)
    setProjects(prev => prev.filter(project => project.id !== projectId))
    toast.info('Project removed from starred')
  }

  const handleEditClick = (project: Project, event: React.MouseEvent) => {
    if (!canUpdateProject) {
      toast.error('You do not have permission to update projects')
      return
    }
    event.stopPropagation()
    setProjectToEdit(project)
    // Convert draft status to inactive since draft is no longer available
    const projectStatus = project.status === 'draft' ? 'inactive' : project.status
    setEditProject({
      name: project.name,
      description: project.description,
      status: projectStatus as 'active' | 'inactive',
      category: project.category,
      tags: project.tags
    })
    setShowEditModal(true)
  }

  const handleDeleteClick = (project: Project, event: React.MouseEvent) => {
    if (!canDeleteProject) {
      toast.error('You do not have permission to delete projects')
      return
    }
    event.stopPropagation()
    setProjectToDelete(project)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!canDeleteProject) {
      toast.error('You do not have permission to delete projects')
      return
    }
    if (projectToDelete) {
      try {
        setIsLoading(true)
        const response = await delete_project(projectToDelete.id)
        
        // Check if response has a message (could be success or error message)
        const responseMessage = response?.message || response?.data?.message
        
        if (responseMessage) {
          // Show the message from API response
          // Check if message indicates failure (contains words like "cannot", "failed", "error", etc.)
          const isError = responseMessage.toLowerCase().includes('cannot') || 
                         responseMessage.toLowerCase().includes('failed') || 
                         responseMessage.toLowerCase().includes('error') ||
                         responseMessage.toLowerCase().includes('not allowed')
          
          if (isError || !response?.status) {
            toast.error(responseMessage)
          } else {
            toast.success(responseMessage)
            // Only remove from list if it was actually deleted
            // Also remove from starred if it was starred
            if (getStarredProjectIds().includes(projectToDelete.id)) {
              toggleStarredProject(projectToDelete.id)
            }
            setProjects(projects.filter(proj => proj.id !== projectToDelete.id))
            setShowDeleteModal(false)
            setProjectToDelete(null)
          }
        } else if (response?.status) {
          // No message but status is true - assume success
          toast.success('Project deleted successfully')
          // Also remove from starred if it was starred
          if (getStarredProjectIds().includes(projectToDelete.id)) {
            toggleStarredProject(projectToDelete.id)
          }
          setProjects(projects.filter(proj => proj.id !== projectToDelete.id))
          setShowDeleteModal(false)
          setProjectToDelete(null)
        } else {
          toast.error('Failed to delete project')
        }
      } catch (error: any) {
        console.error('Error deleting project:', error)
        // Check if error response has a message
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete project'
        toast.error(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleEditInputChange = (field: string, value: string) => {
    setEditProject(prev => ({ ...prev, [field]: value }))
  }

  const handleUpdateProject = async () => {
    if (!canUpdateProject) {
      toast.error('You do not have permission to update projects')
      return
    }
    if (!projectToEdit) return
    
    // Validation for required fields
    if (!editProject.name.trim() || !editProject.status) {
      toast.error('Please fill all required fields')
      return
    }

    try {
      setIsLoading(true)
      // Prepare API payload
      // Convert status to "0"/"1" format as string: active="1", inactive="0"
      const statusValue = editProject.status === 'active' ? '1' : '0'
      
      const apiPayload = {
        project_name: editProject.name,
        description: editProject.description || '',
        status: statusValue,
        // Additional fields from UI that will be sent
        category: editProject.category || '',
        tags: editProject.tags || []
      }
      
      const response = await update_project(projectToEdit.id, apiPayload)
      toast.success('Project updated successfully')
      setShowEditModal(false)
      setProjectToEdit(null)
      // Refresh the starred projects list
      await fetchStarredProjects()
    } catch (error: any) {
      console.error('Error updating project:', error)
      // Show backend error message if available
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update project'
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: {class: 'badge-light-success', text: 'Active'},
      inactive: {class: 'badge-light-danger', text: 'Inactive'},
      draft: {class: 'badge-light-warning', text: 'Draft'}
    }
    
    const config = statusConfig[status as keyof typeof statusConfig]
    return (
      <span className={`badge ${config.class} fw-bold`}>
        {config.text}
      </span>
    )
  }

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  if (!canViewProjects) {
    return (
      <div className='container-xxl py-10'>
        <div className='alert alert-warning'>
          You do not have permission to view starred projects.
        </div>
      </div>
    )
  }

  return (
    <div className='d-flex flex-column flex-column-fluid'>
      {/* Loading Progress Bar */}
      <LoadingProgressBar isLoading={isLoading} />
      
      {/* Header */}
      <div className='toolbar' id='kt_toolbar'>
        <div id='kt_toolbar_container' className='container-fluid d-flex flex-stack justify-content-start align-items-start'>
          <div className='page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0 flex-shrink-0'>
            <button 
              className='btn btn-sm btn-light me-3 d-flex align-items-center'
              onClick={() => navigate('/projects/list')}
              style={{ cursor: 'pointer' }}
            >
              <KTIcon iconName='arrow-left' className='fs-2 me-2' />
              <span>Back to Projects</span>
            </button>
            <h1 className='d-flex align-items-center text-dark fw-bolder fs-3 my-1'>
              <KTIcon iconName='star' className='fs-2 text-warning me-3' />
              Starred Projects
              <span className='h-20px border-gray-200 border-start ms-3 mx-2'></span>
              <small className='text-muted fs-7 fw-bold my-1 ms-1'>Your favorite projects</small>
            </h1>
          </div>
          <div className='d-flex align-items-center gap-2 gap-lg-3'>
            <span className='badge badge-light-warning fs-7 fw-bold'>
              {projects.length} Starred
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div id='kt_content' className='content flex-column-fluid'>
        <div className='container-xxl'>
          {/* Search and Filter */}
          <div className='card mb-5'>
            <div className='card-body'>
              <div className='d-flex align-items-center position-relative my-1'>
                <KTIcon iconName='magnifier' className='fs-3 position-absolute ms-5' />
                <input
                  type='text'
                  className='form-control form-control-solid w-250px ps-13'
                  placeholder='Search starred projects...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Projects Grid */}
          <div className='row g-5 g-xl-8'>
            {isLoading && projects.length === 0 ? (
              // Show skeleton loaders while loading
              <>
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
              </>
            ) : (
              filteredProjects.map((project) => (
              <div key={project.id} className='col-xl-4 col-lg-6 col-md-6'>
                <KTCard className='card-xl-stretch mb-xl-8 h-100'>
                  <KTCardBody className='d-flex flex-column p-6'>
                    {/* Header */}
                    <div className='d-flex align-items-center justify-content-between mb-4'>
                      <div className='d-flex align-items-center'>
                        <div className='symbol symbol-50px me-3'>
                          <div className='symbol-label bg-light-warning'>
                            <KTIcon iconName='star' className='fs-2x text-warning' />
                          </div>
                        </div>
                        <div>
                          <h3 className='fs-4 fw-bolder text-gray-900 mb-1'>{project.name}</h3>
                          <span className='text-muted fw-bold text-muted d-block fs-7'>
                            {project.category}
                          </span>
                        </div>
                      </div>
                      <div className='btn-group'>
                        <button
                          type='button'
                          className='btn btn-sm btn-warning'
                          onClick={(e) => toggleStar(project.id, e)}
                          title='Remove from starred'
                        >
                          <KTIcon iconName='star' className='fs-5' />
                        </button>
                        {(canUpdateProject || canDeleteProject) && (
                          <>
                            <button
                              type='button'
                              className='btn btn-sm btn-light dropdown-toggle dropdown-toggle-split'
                              data-bs-toggle='dropdown'
                              aria-expanded='false'
                            >
                              <span className='visually-hidden'>Toggle Dropdown</span>
                            </button>
                            <ul className='dropdown-menu dropdown-menu-end'>
                              {/* {canViewProjectLogs && (
                                <li>
                                  <button 
                                    className='dropdown-item' 
                                    onClick={() => navigate("/projects/single-project-log", { state: { id: project.id } })}
                                  >
                                    <i className='bi bi-journal-text fs-6 me-2'></i>
                                    Activity Logs
                                  </button>
                                </li>
                              )}
                              {canViewProjectLogs && (canUpdateProject || canDeleteProject) && (
                                <li>
                                  <hr className='dropdown-divider' />
                                </li>
                              )} */}
                              {canUpdateProject && (
                                <li>
                                  <button 
                                    className='dropdown-item' 
                                    onClick={(e) => handleEditClick(project, e)}
                                  >
                                    <KTIcon iconName='pencil' className='fs-6 me-2' />
                                    Edit
                                  </button>
                                </li>
                              )}
                              {canUpdateProject && canDeleteProject && (
                                <li>
                                  <hr className='dropdown-divider' />
                                </li>
                              )}
                              {canDeleteProject && (
                                <li>
                                  <button 
                                    className='dropdown-item text-danger' 
                                    onClick={(e) => handleDeleteClick(project, e)}
                                  >
                                    <KTIcon iconName='trash' className='fs-6 me-2' />
                                    Delete
                                  </button>
                                </li>
                              )}
                            </ul>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <p 
                      className='text-gray-600 fs-6 mb-4 flex-grow-1' 
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      title={project.description || 'N/A'}
                    >
                      {project.description || 'N/A'}
                    </p>

                    {/* Categories Tags */}
                    <div className='mb-4'>
                      {(projectCategories[project.id] || [])
                        .filter((cat: any) => cat.status === 1 || cat.status === '1')
                        .map((cat: any) => (
                          <span key={cat.id} className='badge badge-light-primary me-1 mb-1'>
                            {cat.category_name}
                          </span>
                        ))}
                    </div>

                    {/* Stats */}
                    <div className='row mb-4'>
                      <div className='col-6'>
                        <div className='d-flex align-items-center'>
                          <KTIcon iconName='copy' className='fs-3 text-primary me-2' />
                          <div>
                            <div className='fs-6 fw-bolder text-gray-900'>{project.totalUrls}</div>
                            <div className='fs-7 text-muted'>Total URLs</div>
                          </div>
                        </div>
                      </div>
                      <div className='col-6'>
                        <div className='d-flex align-items-center'>
                          <KTIcon iconName='check-circle' className='fs-3 text-success me-2' />
                          <div>
                            <div className='fs-6 fw-bolder text-gray-900'>{project.activeUrls}</div>
                            <div className='fs-7 text-muted'>Active URLs</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Status and Action */}
                    <div className='d-flex align-items-center justify-content-between'>
                      {getStatusBadge(project.status)}
                      <button
                        className='btn btn-sm btn-primary'
                        onClick={() => handleProjectClick(project.id)}
                      >
                        View Details
                        <KTIcon iconName='arrow-right' className='fs-2 ms-1' />
                      </button>
                    </div>
                  </KTCardBody>
                </KTCard>
              </div>
            ))
            )}
          </div>

          {/* Empty State */}
          {!isLoading && filteredProjects.length === 0 && (
            <div className='text-center py-10'>
              <KTIcon iconName='star' className='fs-3x text-muted mb-5' />
              <h3 className='text-gray-900 mb-3'>
                {searchTerm ? 'No starred projects found' : 'No starred projects yet'}
              </h3>
              <p className='text-muted fs-6'>
                {searchTerm 
                  ? 'Try adjusting your search terms' 
                  : 'Star projects from the main projects list to see them here'
                }
              </p>
              {!searchTerm && (
                <button 
                  className='btn btn-primary'
                  onClick={() => navigate('/projects/list')}
                >
                  Browse Projects
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Project Modal */}
      {canUpdateProject && showEditModal && projectToEdit && (
        <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h2 className='modal-title'>Edit Project</h2>
                <button 
                  type='button' 
                  className='btn-close' 
                  onClick={() => {
                    setShowEditModal(false)
                    setProjectToEdit(null)
                  }}
                ></button>
              </div>
              <div className='modal-body'>
                <form>
                  <div className='mb-5'>
                    <label className='form-label fw-bold'>Project Name <span className='text-danger'>*</span></label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Enter project name'
                      value={editProject.name}
                      onChange={(e) => handleEditInputChange('name', e.target.value)}
                      required
                    />
                  </div>
                  <div className='mb-5'>
                    <label className='form-label fw-bold'>Description</label>
                    <textarea
                      className='form-control'
                      rows={3}
                      placeholder='Enter project description'
                      value={editProject.description}
                      onChange={(e) => handleEditInputChange('description', e.target.value)}
                    ></textarea>
                  </div>
                  <div className='mb-5'>
                    <label className='form-label fw-bold'>Status <span className='text-danger'>*</span></label>
                    <select
                      className='form-select'
                      value={editProject.status}
                      onChange={(e) => handleEditInputChange('status', e.target.value)}
                    >
                      <option value='active'>Active</option>
                      <option value='inactive'>Inactive</option>
                    </select>
                  </div>
                </form>
              </div>
              <div className='modal-footer'>
                <button 
                  type='button' 
                  className='btn btn-light' 
                  onClick={() => {
                    setShowEditModal(false)
                    setProjectToEdit(null)
                  }}
                >
                  Cancel
                </button>
                <button 
                  type='button' 
                  className='btn btn-primary' 
                  onClick={handleUpdateProject}
                >
                  Update Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        className='modal fade'
        id='kt_modal_delete_project'
        tabIndex={-1}
        role='dialog'
        show={canDeleteProject && showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false)
          setProjectToDelete(null)
        }}
      >
        <div className='modal-dialog modal-dialog-centered mw-650px'>
          <div className='modal-content'>
            <div className='modal-header pb-0 border-0 justify-content-end'>
              <div
                className='btn btn-sm btn-icon btn-active-color-primary'
                onClick={() => {
                  setShowDeleteModal(false)
                  setProjectToDelete(null)
                }}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </div>
            </div>
            <div className='modal-body scroll-y mx-5 mx-xl-18 pt-0 pb-15'>
              <div className='mb-13 text-center'>
                <KTIcon iconName='question' className='fs-5tx text-danger mb-5' />
                <h1 className='mb-3'>Delete Project</h1>
                <div className='text-muted fw-semibold fs-5'>
                  Are you sure you want to delete this project?
                  {projectToDelete && (
                    <div className='mt-3 mb-3'>
                      <strong className='text-dark fs-4'>"{projectToDelete.name}"</strong>
                    </div>
                  )}
                  <div className='mt-4'>
                    <p className='mb-2'>This action cannot be undone.</p>
                    <p className='mb-0'>All subprojects and associated data in this project will also be permanently deleted.</p>
                  </div>
                </div>
              </div>
              <div className='d-flex flex-stack'>
                <button
                  className='btn btn-primary'
                  onClick={() => {
                    setShowDeleteModal(false)
                    setProjectToDelete(null)
                  }}
                >
                  Cancel
                </button>
                <button className='btn btn-danger' onClick={handleDeleteConfirm}>
                  Delete Project
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export {StarredProjects}

